import React, { ChangeEvent, useRef, useState } from 'react';
import EPDService from 'services/EpdService';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Container, H1, H3, MainView, TextSmall } from 'styles/Styles.styled';
import OverlaySpinner from 'components/spinners/OverlaySpinner';
import { LcaXLValidationError } from 'types/lca-results/LcaXLValidationError';
import WizardLcaResultsErrors from 'components/wizard/WizardLcaResultsErrors';
import { LcaXLValidationErrorCode } from 'types/lca-results/LcaXLValidationErrorCode';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { LCAResultsOutdatedTemplateDialog } from 'components/v2/dialogs/LCAResultsOutdatedTemplateDialog';
import { toaster } from 'components/v2/toast';
import { ErrorDetails } from 'styles/v2/Styles.styled';

const LcaValidation: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const inputFileRef = useRef(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<LcaXLValidationError[] | null>(null);

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const targetFile = target && target.files && target.files[0];
    if (targetFile instanceof File) {
      const fileName = targetFile.name;
      const fileType = targetFile.type;

      const reader = new FileReader();
      reader.onload = async () => {
        const fileBlob = reader.result as ArrayBuffer;
        if (fileBlob) {
          const blob = new Blob([new Uint8Array(fileBlob)], {
            type: fileType,
          });
          if (blob.size > 1024 * 1024 * 10) {
            alert('You can not upload file more than 10 mb');
            return;
          }
          try {
            setIsLoading(true);

            const result = await EPDService.validateLCAFile(fileName, blob);
            if (result) {
              if (result[0]?.code === LcaXLValidationErrorCode.OUTDATED_TEMPLATE) {
                LCAResultsOutdatedTemplateDialog();
              }
              if (result.some((e) => e.code === LcaXLValidationErrorCode.INVALID_VALUE_MAX_LENGTH)) {
                toaster({
                  severity: 'error',
                  summary: 'Character limit exceeded in the uploaded file.',
                  details: <ErrorDetails>Please review the instructions, update the file, and try again.</ErrorDetails>,
                });
              }
              setErrors(result);
              return;
            } else {
              alert('oops! something went wrong.');
            }
          } catch {
            alert('oops! something went wrong.');
          } finally {
            setIsLoading(false);
          }
        }
      };
      reader.readAsArrayBuffer(targetFile);
    }
  };

  const selectFile = () => {
    (inputFileRef as any).current.click();
  };

  const onFileInputChange = (e: any, validExtensions: Array<string>) => {
    setErrors(null);

    if (validExtensions?.length > 0) {
      for (const file of e.target.files) {
        const fileExtension = file.name.split('.').pop().toLowerCase();

        if (!validExtensions.includes(fileExtension)) {
          alert(`Please upload a file with the allowed extension(s): ${validExtensions.join('/')}.`);
          return false;
        }
      }
    }

    uploadFile(e);
  };

  return (
    <Container>
      <ConfirmDialog />
      <MainView>
        {isLoading && (
          <center>
            <div>
              <OverlaySpinner />
            </div>
          </center>
        )}
        <ValidationContainer>
          <Title>LCA validation</Title>
          <div
            style={{
              display: 'flex',
            }}
          >
            <FileBox>
              <TextSmall>You can upload max&nbsp;10&nbsp;MB</TextSmall>
              <input
                ref={inputFileRef as any}
                type="file"
                accept=".xls,.xlsx"
                value={''}
                onChange={(e) => onFileInputChange(e, ['xls', 'xlsx'])}
                title=""
              />
              <UploadLabel onClick={() => selectFile()}>{t('upload')}</UploadLabel>
            </FileBox>
          </div>
        </ValidationContainer>
        {errors !== null && (
          <ValidationContainer>
            <div>
              {errors.length === 0 ? (
                <H3 style={{ color: 'green' }}>Document is valid</H3>
              ) : (
                <WizardLcaResultsErrors errors={errors} />
              )}
            </div>
          </ValidationContainer>
        )}
      </MainView>
    </Container>
  );
};

const FileBox = styled.div`
  padding: 1rem 3rem;
  flex: 1 0 auto;

  > h3 {
    margin-top: 0;
  }

  > input[type='file'] {
    visibility: hidden;
    display: none;
  }

  > p {
    font-weight: bold;
  }
`;

const UploadLabel = styled.label`
  font-size: 0.8rem;
  padding: 0.5rem;
  margin: 0.5rem 0;
  background: ${(props) => props.theme.colors.orange};
  border-radius: 5px;
  color: white;
  cursor: pointer;

  :hover {
    background: ${(props) => props.theme.colors.lightOrange};
  }
`;

const ValidationContainer = styled(Container)`
  flex: 1;
  justify-content: center;
  margin: 10px auto;
`;

const Title = styled(H1)`
  border-right: 1px solid rgb(142, 142, 142);
  font-size: 3rem;
  padding-right: 2rem;
`;

export default LcaValidation;
